import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  EntityState,
  PayloadAction
} from '@reduxjs/toolkit'

import {
  EmployeeData,
  GetListAPIPayload
} from 'features/employees/employeeSlice'
import { RootState } from 'app/store'
import { getEmployeesAPI } from 'utils/api'
import { ErrorData, ErrorState } from 'features/error/errorSlice'
import { SelectionType } from 'utils/constant/filterStatus'

const employeesAdapter = createEntityAdapter<EmployeeData>()

export interface EmployeeGroupState {
  preview: {
    employees: EntityState<EmployeeData>
    isLoading: boolean
    errors: ErrorData
    total: number
    currentPage: number
    itemsPerPage: number
    filter: {
      query: string
      status: string
    }
  }
  addGroup: {
    select: {
      employees: EntityState<EmployeeData>
    }
  }
  editGroup: {
    select: {
      employees: EntityState<EmployeeData>
    }
    list: {
      employees: EntityState<EmployeeData>
      total: number
      limit: number
      errors: ErrorData
      isLoading: boolean
      fetchCount: number
    }
  }
}

export const initialState: EmployeeGroupState = {
  preview: {
    employees: employeesAdapter.getInitialState(),
    isLoading: true,
    errors: null,
    currentPage: 1,
    itemsPerPage: 10,
    total: 0,
    filter: {
      status: SelectionType.ALL,
      query: ''
    }
  },
  addGroup: {
    select: {
      employees: employeesAdapter.getInitialState()
    }
  },
  editGroup: {
    select: {
      employees: employeesAdapter.getInitialState()
    },
    list: {
      employees: employeesAdapter.getInitialState(),
      total: 0,
      errors: null,
      isLoading: false,
      limit: 500,
      fetchCount: 1
    }
  }
}

function employeeGroupThunk(name: string) {
  return createAsyncThunk(
    name,
    async (payload: GetListAPIPayload, { rejectWithValue }) => {
      try {
        return await getEmployeesAPI({
          ...payload
        })
      } catch (error) {
        const { status, data } = error.response
        if (data || status) return rejectWithValue({ data, status })
        return error
      }
    },
    {
      condition: (_, { getState }) => {
        const { error } = getState() as {
          error: ErrorState
        }
        if (
          error.status === 429 &&
          Math.floor(Date.now() / 1000) <= error.updateAt!
        ) {
          return false
        }
      },
      dispatchConditionRejection: true
    }
  )
}

export const getEmployeeGroupPreview = employeeGroupThunk(
  'employeeGroup/getEmployeeGroupPreview'
)

export const getEmployeeEditGroupListed = employeeGroupThunk(
  'employeeGroup/getEmployeeEditGroupList'
)

export const EmployeeGroupSlice = createSlice({
  name: 'employeeGroup',
  initialState: initialState,
  reducers: {
    selectEmployeesAddGroup: (state, action) => {
      employeesAdapter.setAll(state.addGroup.select.employees, action.payload)
    },
    selectEmployeesEditGroup: (state, action) => {
      employeesAdapter.setAll(state.editGroup.select.employees, action.payload)
    },
    setCurrentPagePreview: (state, action) => {
      state.preview.currentPage = action.payload
    },
    setItemsPerPagePreview: (state, action) => {
      state.preview.itemsPerPage = action.payload
    },
    setFilterPreview: (state, action) => {
      const { name, value } = action.payload
      if (name === 'SEARCH_QUERY') {
        state.preview.filter.query = value
      } else if (name === 'STATUS') {
        state.preview.filter.status = value
      }
    },
    resetState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEmployeeGroupPreview.fulfilled, (state, action) => {
        const { employees, total } = action.payload.data
        state.preview.isLoading = false
        state.preview.errors = null
        state.preview.total = total
        employeesAdapter.setAll(state.preview.employees, employees)
      })
      .addCase(
        getEmployeeGroupPreview.rejected,
        (state, action: PayloadAction<any, string, any, any>) => {
          state.preview.isLoading = false
          state.preview.errors = action.payload || {
            status: 500,
            data: `${action.error.name}: ${action.error.message}`
          }
        }
      )
      .addCase(getEmployeeEditGroupListed.pending, (state) => {
        state.editGroup.list.isLoading = true
      })
      .addCase(getEmployeeEditGroupListed.fulfilled, (state, action) => {
        const { employees, total } = action.payload.data
        state.editGroup.list.isLoading = false
        state.editGroup.list.errors = null
        state.editGroup.list.total = total
        employeesAdapter.upsertMany(state.editGroup.select.employees, employees)
        employeesAdapter.upsertMany(state.editGroup.list.employees, employees)
        state.editGroup.list.fetchCount += 1
        if (
          total >=
          state.editGroup.list.fetchCount * state.editGroup.list.limit
        ) {
          state.editGroup.list.isLoading = true
        } else {
          state.editGroup.list.isLoading = false
        }
      })
      .addCase(
        getEmployeeEditGroupListed.rejected,
        (state, action: PayloadAction<any, string, any, any>) => {
          state.editGroup.list.isLoading = false
          state.editGroup.list.errors = action.payload || {
            status: 500,
            data: `${action.error.name}: ${action.error.message}`
          }
        }
      )
  }
})

export const {
  selectEmployeesAddGroup,
  selectEmployeesEditGroup,
  setCurrentPagePreview,
  setItemsPerPagePreview,
  setFilterPreview,
  resetState
} = EmployeeGroupSlice.actions

export const employeesGroupPreviewSelector = employeesAdapter.getSelectors(
  (state: RootState) => state.employeeGroup.preview.employees
)
export const employeesAddGroupSelectedSelector = employeesAdapter.getSelectors(
  (state: RootState) => state.employeeGroup.addGroup.select.employees
)
export const employeesEditGroupListedSelector = employeesAdapter.getSelectors(
  (state: RootState) => state.employeeGroup.editGroup.list.employees
)
export const employeesEditGroupSelectedSelector = employeesAdapter.getSelectors(
  (state: RootState) => state.employeeGroup.editGroup.select.employees
)
export const employeesGroupPreviewStatusSelector = (state: RootState) =>
  state.employeeGroup.preview
export const employeesEditGroupListedStatusSelector = (state: RootState) =>
  state.employeeGroup.editGroup.list

export default EmployeeGroupSlice.reducer
